// Diagramme als Inline-SVG.
//
// Bewusst ohne Chart-Bibliothek: die drei Formen, die diese App braucht
// (Linie, Balken, Ring), sind ein paar Zeilen Geometrie – eine 200-kB-Abhängigkeit
// wäre schwerer als das gesamte übrige Frontend.
const NS = 'http://www.w3.org/2000/svg';

function svgEl<K extends keyof SVGElementTagNameMap>(
  tag: K,
  attrs: Record<string, string | number> = {},
): SVGElementTagNameMap[K] {
  const node = document.createElementNS(NS, tag);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, String(v));
  return node;
}

export interface Point {
  date: string;
  value: number | null;
}

export interface LineChartOptions {
  points: Point[];
  height?: number;
  /** Zweite, gestrichelte Linie (z.B. gleitender Mittelwert). */
  trend?: Point[];
  /** Waagerechte Ziellinie. */
  target?: number | null;
  formatValue?: (v: number) => string;
  label?: string;
}

const W = 320; // internes Koordinatensystem; das SVG skaliert per viewBox

/** Linienchart mit optionaler Trendlinie und Ziellinie (z.B. Gewicht, kcal). */
export function lineChart({
  points,
  height = 140,
  trend,
  target = null,
  formatValue = (v) => String(Math.round(v)),
  label = 'Verlauf',
}: LineChartOptions): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'chart';

  const real = points.filter((p) => p.value != null) as { date: string; value: number }[];
  if (real.length < 2) {
    wrap.append(emptyHint('Noch zu wenig Daten für einen Verlauf.'));
    return wrap;
  }

  const values = real.map((p) => p.value);
  if (target != null) values.push(target);
  let min = Math.min(...values);
  let max = Math.max(...values);
  if (max - min < 1e-6) {
    min -= 1;
    max += 1;
  }
  const pad = (max - min) * 0.12;
  min -= pad;
  max += pad;

  const padL = 34;
  const padR = 6;
  const padT = 8;
  const padB = 18;
  const innerW = W - padL - padR;
  const innerH = height - padT - padB;

  const x = (i: number) => padL + (i / (points.length - 1)) * innerW;
  const y = (v: number) => padT + (1 - (v - min) / (max - min)) * innerH;

  const svg = svgEl('svg', {
    viewBox: `0 0 ${W} ${height}`,
    role: 'img',
    'aria-label': `${label}: ${formatValue(real[0]!.value)} bis ${formatValue(real[real.length - 1]!.value)}`,
  });

  // Hilfslinien mit Beschriftung
  for (const frac of [0, 0.5, 1]) {
    const value = max - frac * (max - min);
    const yy = padT + frac * innerH;
    svg.append(
      svgEl('line', { class: 'chart-grid', x1: padL, x2: W - padR, y1: yy, y2: yy }),
      text(formatValue(value), padL - 5, yy + 3, 'end'),
    );
  }

  if (target != null) {
    svg.append(
      svgEl('line', {
        class: 'chart-target',
        x1: padL,
        x2: W - padR,
        y1: y(target),
        y2: y(target),
      }),
    );
  }

  // Fläche unter der Linie – gibt dem Verlauf Gewicht, ohne aufdringlich zu sein
  const path = linePath(points, x, y);
  if (path) {
    const firstIndex = points.findIndex((p) => p.value != null);
    const lastIndex = points.length - 1 - [...points].reverse().findIndex((p) => p.value != null);
    svg.append(
      svgEl('path', {
        class: 'chart-area',
        d: `${path} L ${x(lastIndex)} ${padT + innerH} L ${x(firstIndex)} ${padT + innerH} Z`,
      }),
      svgEl('path', { class: 'chart-line', d: path }),
    );
  }

  if (trend?.length) {
    const trendPath = linePath(trend, x, y);
    if (trendPath) svg.append(svgEl('path', { class: 'chart-line-soft', d: trendPath }));
  }

  // Endpunkt markieren
  const lastIdx = points.length - 1 - [...points].reverse().findIndex((p) => p.value != null);
  const lastVal = points[lastIdx]?.value;
  if (lastVal != null) {
    svg.append(svgEl('circle', { class: 'chart-dot', cx: x(lastIdx), cy: y(lastVal), r: 3.5 }));
  }

  // Erste und letzte Datumsbeschriftung
  svg.append(
    text(shortDate(points[0]!.date), padL, height - 4, 'start'),
    text(shortDate(points[points.length - 1]!.date), W - padR, height - 4, 'end'),
  );

  wrap.append(svg);
  return wrap;
}

/** Verbindet nur zusammenhängende Abschnitte – Lücken bleiben Lücken. */
function linePath(
  points: Point[],
  x: (i: number) => number,
  y: (v: number) => number,
): string | null {
  const parts: string[] = [];
  let open = false;
  points.forEach((p, i) => {
    if (p.value == null) {
      open = false;
      return;
    }
    parts.push(`${open ? 'L' : 'M'} ${x(i).toFixed(1)} ${y(p.value).toFixed(1)}`);
    open = true;
  });
  return parts.length > 1 ? parts.join(' ') : null;
}

export interface BarChartOptions {
  bars: { label: string; value: number; highlight?: boolean }[];
  height?: number;
  formatValue?: (v: number) => string;
  label?: string;
}

/** Balkenchart für Monatswerte (Ausgaben, Verschwendung). */
export function barChart({
  bars,
  height = 140,
  formatValue = (v) => String(Math.round(v)),
  label = 'Werte',
}: BarChartOptions): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'chart';

  if (!bars.length) {
    wrap.append(emptyHint('Noch keine Daten.'));
    return wrap;
  }

  const max = Math.max(...bars.map((b) => b.value), 1);
  const padL = 34;
  const padR = 6;
  const padT = 8;
  const padB = 20;
  const innerW = W - padL - padR;
  const innerH = height - padT - padB;
  const slot = innerW / bars.length;
  const barW = Math.min(slot * 0.62, 26);

  const svg = svgEl('svg', {
    viewBox: `0 0 ${W} ${height}`,
    role: 'img',
    'aria-label': `${label}, Höchstwert ${formatValue(max)}`,
  });

  for (const frac of [0, 0.5, 1]) {
    const yy = padT + frac * innerH;
    svg.append(
      svgEl('line', { class: 'chart-grid', x1: padL, x2: W - padR, y1: yy, y2: yy }),
      text(formatValue(max - frac * max), padL - 5, yy + 3, 'end'),
    );
  }

  bars.forEach((bar, i) => {
    const h = Math.max(2, (bar.value / max) * innerH);
    const cx = padL + slot * i + slot / 2;
    svg.append(
      svgEl('rect', {
        class: bar.highlight ? 'chart-bar chart-bar-over' : 'chart-bar',
        x: cx - barW / 2,
        y: padT + innerH - h,
        width: barW,
        height: h,
      }),
    );
    // Bei vielen Balken nur jeden zweiten beschriften, sonst wird es Brei.
    if (bars.length <= 8 || i % 2 === 0) {
      svg.append(text(bar.label, cx, height - 5, 'middle'));
    }
  });

  wrap.append(svg);
  return wrap;
}

export interface RingOptions {
  value: number;
  max: number;
  label: string;
  /** Anzeigetext in der Mitte; ohne ihn wird der Wert gezeigt. */
  display?: string;
  size?: number;
  color?: string;
}

/** Fortschrittsring (Kalorien, Protein …). */
export function ring({
  value,
  max,
  label,
  display,
  size = 76,
  color = 'var(--brand)',
}: RingOptions): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'ring';

  const stroke = 7;
  const r = (size - stroke) / 2;
  const circumference = 2 * Math.PI * r;
  const ratio = max > 0 ? Math.min(value / max, 1) : 0;
  const over = max > 0 && value > max;

  const svg = svgEl('svg', {
    width: size,
    height: size,
    viewBox: `0 0 ${size} ${size}`,
    role: 'img',
    'aria-label': `${label}: ${Math.round(value)} von ${Math.round(max)}`,
  });

  svg.append(
    svgEl('circle', {
      class: 'ring-track',
      cx: size / 2,
      cy: size / 2,
      r,
      fill: 'none',
      'stroke-width': stroke,
    }),
    svgEl('circle', {
      class: 'ring-value',
      cx: size / 2,
      cy: size / 2,
      r,
      fill: 'none',
      stroke: over ? 'var(--warn)' : color,
      'stroke-width': stroke,
      'stroke-dasharray': circumference,
      'stroke-dashoffset': circumference * (1 - ratio),
    }),
  );

  const number = document.createElement('div');
  number.className = 'ring-number';
  number.textContent = display ?? String(Math.round(value));

  const caption = document.createElement('div');
  caption.className = 'ring-label';
  caption.textContent = label;

  wrap.append(svg, number, caption);
  return wrap;
}

function text(content: string, x: number, y: number, anchor: string): SVGTextElement {
  const node = svgEl('text', { class: 'chart-label', x, y, 'text-anchor': anchor });
  node.textContent = content;
  return node;
}

function shortDate(iso: string): string {
  const [, m, d] = iso.split('-');
  return d && m ? `${Number(d)}.${Number(m)}.` : iso;
}

function emptyHint(message: string): HTMLElement {
  const p = document.createElement('p');
  p.className = 'tiny';
  p.textContent = message;
  return p;
}
